import { z } from "zod";
import { WorkOrderState } from "./enums";

const Id = z.string().min(1).max(64);

/**
 * Vertrag des taskFLOW-Connectors (docs/08). taskFLOW ruft szyOS über `/integrations/v1/taskflow/*` auf,
 * szyOS meldet Zustände an den Webhook von taskFLOW. Beide Richtungen sind mit HMAC-SHA256 signiert.
 */

/** Kopfzeilen der Signatur in beiden Richtungen. */
export const INTEGRATION_HEADERS = {
  timestamp: "x-szyos-timestamp",
  signature: "x-szyos-signature",
} as const;

/**
 * Zweck einer signierten Nachricht. Er gehört zur Signatur, damit eine abgefangene Nachricht weder an einem
 * anderen Endpunkt noch in der Gegenrichtung gilt.
 */
export const INTEGRATION_PURPOSES = {
  projects: "GET /integrations/v1/taskflow/projects",
  workOrders: "POST /integrations/v1/taskflow/work-orders",
  update: "POST /integrations/v1/taskflow/work-orders/update",
  attachments: "POST /integrations/v1/taskflow/attachments",
  status: "POST taskflow-status",
} as const;

export const TaskflowWorkOrderInput = z.strictObject({
  /** ID der Task in taskFLOW. */
  taskId: z.uuid(),
  /** Stand der Task (`updated_at`), gehört zum Idempotenzschlüssel. */
  revision: z.string().min(1).max(64),
  /** Zugeordnetes szyOS-Projekt. */
  projectId: Id,
  title: z
    .string()
    .trim()
    .min(1)
    .max(200)
    .refine(
      (t) => [...t].every((c) => c.charCodeAt(0) >= 32 && c.charCodeAt(0) !== 127),
      "keine Zeilenumbrüche oder Steuerzeichen im Titel",
    ),
  description: z.string().max(4000).default(""),
  /** Checkliste der Task, wird zu Muss-Funktionen und Akzeptanzkriterien. */
  checklist: z.array(z.string().trim().min(1).max(500)).max(50).default([]),
  taskUrl: z.url({ protocol: /^https$/ }).max(500),
  /** Anzeigename der Person, die in taskFLOW gesendet hat (nur Information). */
  requestedBy: z.string().max(200).default(""),
});
export type TaskflowWorkOrderInput = z.infer<typeof TaskflowWorkOrderInput>;

export const TASKFLOW_STATUSES = [
  "accepted",
  "running",
  "needs_decision",
  "blocked",
  "pr_ready",
  "completed",
  "failed",
] as const;
export const TaskflowStatus = z.enum(TASKFLOW_STATUSES);
export type TaskflowStatus = z.infer<typeof TaskflowStatus>;

export const TaskflowWorkOrderResponse = z.object({
  workOrderId: Id,
  status: TaskflowStatus,
  szyosUrl: z.string(),
  /** `true`, wenn die Task schon vorher gesendet wurde und der bestehende Auftrag zurückkommt. */
  existing: z.boolean(),
});
export type TaskflowWorkOrderResponse = z.infer<typeof TaskflowWorkOrderResponse>;

/** Was szyOS mit einer geänderten Task gemacht hat (Aktualisieren aus taskFLOW). */
export const TaskflowUpdateOutcome = z.enum(["unchanged", "spec_updated", "decision", "new_order"]);
export type TaskflowUpdateOutcome = z.infer<typeof TaskflowUpdateOutcome>;

export const TaskflowUpdateResponse = z.object({
  workOrderId: Id,
  status: TaskflowStatus,
  szyosUrl: z.string(),
  outcome: TaskflowUpdateOutcome,
  /** Kurze Liste der Änderungen gegenüber dem Stand in szyOS. */
  changes: z.array(z.string()),
});
export type TaskflowUpdateResponse = z.infer<typeof TaskflowUpdateResponse>;

/** Anhang einer Task (Bild, PDF, Text), höchstens 5 MB; landet als Material am Auftrag. */
export const MAX_TASKFLOW_ATTACHMENT_BYTES = 5 * 1024 * 1024;
export const TaskflowAttachmentInput = z.strictObject({
  taskId: z.uuid(),
  workOrderId: Id,
  fileName: z.string().trim().min(1).max(200),
  contentBase64: z.string().min(4).max(Math.ceil((MAX_TASKFLOW_ATTACHMENT_BYTES * 4) / 3) + 8),
});
export type TaskflowAttachmentInput = z.infer<typeof TaskflowAttachmentInput>;

export const TaskflowProject = z.object({ id: Id, title: z.string() });
export type TaskflowProject = z.infer<typeof TaskflowProject>;

/** Zustandsmeldung an den Webhook von taskFLOW. */
export const TaskflowStatusEvent = z.object({
  eventId: z.string(),
  /** Monoton steigend je Auftrag; ältere Meldungen verwirft taskFLOW. */
  sequence: z.number().int().nonnegative(),
  taskId: z.uuid(),
  workOrderId: Id,
  status: TaskflowStatus,
  state: WorkOrderState,
  title: z.string(),
  message: z.string(),
  szyosUrl: z.string(),
  prUrls: z.array(z.string()),
  occurredAt: z.string(),
  /** Folgeauftrag: dieser Auftrag ersetzt den genannten; taskFLOW stellt die Verknüpfung um. */
  replacesWorkOrderId: Id.nullable().optional(),
});
export type TaskflowStatusEvent = z.infer<typeof TaskflowStatusEvent>;
